import React from "react";
import { Button, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { AuditFieldChange, AuditLogEntry, AuditRef, AuditSnapshot } from "./types";
import { EMPTY_VALUE, formatAuditValue, formatRef, isCollectionChange } from "./format";

interface Props {
  entry: AuditLogEntry | null;
  onHide: () => void;
}

const formatRefs = (refs: AuditRef[]) => refs.map(formatRef).join(", ") || EMPTY_VALUE;

const ChangeRow = ({ field, change }: { field: string; change: AuditFieldChange }) => {
  if (isCollectionChange(change)) {
    return (
      <tr>
        <td className="fw-semibold">{field}</td>
        <td className="wrap-col text-danger">{change.removed.length ? `− ${formatRefs(change.removed)}` : EMPTY_VALUE}</td>
        <td className="wrap-col text-success">{change.added.length ? `+ ${formatRefs(change.added)}` : EMPTY_VALUE}</td>
      </tr>
    );
  }
  return (
    <tr>
      <td className="fw-semibold">{field}</td>
      <td className="wrap-col text-danger">{formatAuditValue(change.old)}</td>
      <td className="wrap-col text-success">{formatAuditValue(change.new)}</td>
    </tr>
  );
};

const ChangesTable = ({ changes }: { changes: Record<string, AuditFieldChange> }) => {
  const { t } = useTranslation();
  return (
    <table className="table table-sm">
      <thead>
        <tr>
          <th>{t("auditLog.field")}</th>
          <th>{t("auditLog.oldValue")}</th>
          <th>{t("auditLog.newValue")}</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(changes).map(([field, change]) => (
          <ChangeRow key={field} field={field} change={change} />
        ))}
      </tbody>
    </table>
  );
};

const SnapshotTable = ({ snapshot }: { snapshot: AuditSnapshot }) => {
  const { t } = useTranslation();
  const rows: [string, string][] = [
    ...Object.entries(snapshot.fields ?? {}).map(([field, value]): [string, string] => [field, formatAuditValue(value)]),
    ...Object.entries(snapshot.collections ?? {}).map(([field, refs]): [string, string] => [field, formatRefs(refs)]),
  ];
  return (
    <table className="table table-sm">
      <thead>
        <tr>
          <th>{t("auditLog.field")}</th>
          <th>{t("auditLog.value")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([field, value]) => (
          <tr key={field}>
            <td className="fw-semibold">{field}</td>
            <td className="wrap-col">{value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const AuditLogDetailModal = ({ entry, onHide }: Props) => {
  const { t } = useTranslation();
  if (!entry) return null;

  return (
    <Modal show onHide={onHide} size="lg" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          {t(`enums.auditEntityType.${entry.entityType}`, entry.entityType)} #{entry.entityId} ·{" "}
          {t(`enums.auditAction.${entry.action}`, entry.action)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-1">
          <strong>{t("auditLog.source")}:</strong> {entry.source ?? EMPTY_VALUE}
        </p>
        <p className="mb-3">
          <strong>{t("auditLog.ip")}:</strong> {entry.ip ?? EMPTY_VALUE}
        </p>
        {entry.changes ? (
          <ChangesTable changes={entry.changes} />
        ) : entry.snapshot ? (
          <SnapshotTable snapshot={entry.snapshot} />
        ) : (
          <p>{t("auditLog.noDetails")}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          {t("auditLog.close")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AuditLogDetailModal;
